import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.heat";

const US_CENTER = [39.5, -98.35];

/**
 * Lightweight Leaflet map for the admin metric panel.
 * mode "pins" → clustered colored markers; mode "heat" → density heatmap.
 */
export default function MetricLocationMap({ points = [], color = "#ff4500", mode = "pins", height = "320px" }) {
    const elRef = useRef(null);
    const mapRef = useRef(null);
    const layerRef = useRef(null);

    // Init once.
    useEffect(() => {
        if (mapRef.current || !elRef.current) return;
        const map = L.map(elRef.current, {
            center: US_CENTER,
            zoom: 3,
            zoomControl: true,
            attributionControl: true,
            scrollWheelZoom: false,
            worldCopyJump: false,
        });
        L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", {
            attribution: "Tiles © Esri",
            maxZoom: 16,
        }).addTo(map);
        mapRef.current = map;
        // Leaflet needs a size recalc once the container is laid out.
        setTimeout(() => map.invalidateSize(), 150);
        return () => { map.remove(); mapRef.current = null; };
    }, []);

    // Redraw layer on points / mode change.
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
        const valid = points.filter((p) => typeof p.lat === "number" && typeof p.lng === "number");
        if (valid.length === 0) return;

        if (mode === "heat") {
            const heat = L.heatLayer(valid.map((p) => [p.lat, p.lng, 1]), {
                radius: 26, blur: 20, minOpacity: 0.35, maxZoom: 12,
                gradient: { 0.2: "#1e3a8a", 0.4: "#7c3aed", 0.65: color, 1.0: "#ffffff" },
            });
            heat.addTo(map);
            layerRef.current = heat;
        } else {
            const cluster = L.markerClusterGroup({
                showCoverageOnHover: false,
                maxClusterRadius: 40,
                iconCreateFunction: (c) => L.divIcon({
                    html: `<div style="background:${color};color:#fff;border-radius:9999px;width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;border:2px solid #0f0f0f;">${c.getChildCount()}</div>`,
                    className: "", iconSize: [30, 30],
                }),
            });
            valid.forEach((p) => {
                const m = L.circleMarker([p.lat, p.lng], {
                    radius: 6, color: "#0f0f0f", weight: 1.5, fillColor: color, fillOpacity: 0.9,
                });
                const loc = [p.city, p.state].filter(Boolean).join(", ");
                m.bindPopup(`<b>${(p.name || "").replace(/</g, "&lt;")}</b>${loc ? `<br/>${loc}` : ""}`);
                cluster.addLayer(m);
            });
            cluster.addTo(map);
            layerRef.current = cluster;
        }

        try {
            const b = L.latLngBounds(valid.map((p) => [p.lat, p.lng]));
            map.fitBounds(b.pad(0.2), { maxZoom: 9, animate: false });
        } catch { /* single/invalid bounds */ }
    }, [points, mode, color]);

    return <div ref={elRef} data-testid="metric-leaflet" style={{ height, width: "100%" }} className="rounded overflow-hidden" />;
}
